<script setup lang="ts">
  import { ref } from "vue";
  import { Plus, Trash2 } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Label } from "@/components/ui/label";
  import { Select } from "@/components/episto-ui/select";
  import type { SelectOption } from "@/components/episto-ui/select";

  interface CustomBaseRow {
    key: number;
    questionId: number | null;
    baseId: number | null;
  }

  // question_id => base question_id, the shape stored on the export
  const model = defineModel<Record<string, number>>({ required: true });

  defineProps<{
    questionOptions: SelectOption[];
    baseOptions: SelectOption[];
  }>();

  let nextKey = 0;
  // Local rows so a half-filled line can exist before it reaches the model.
  const rows = ref<CustomBaseRow[]>(
    Object.entries(model.value).map(([questionId, baseId]) => ({
      key: ++nextKey,
      questionId: Number(questionId),
      baseId: Number(baseId) || null,
    })),
  );

  const commit = () => {
    const next: Record<string, number> = {};
    rows.value.forEach((row) => {
      if (row.questionId && row.baseId) next[String(row.questionId)] = row.baseId;
    });
    model.value = next;
  };

  const addRow = () => rows.value.push({ key: ++nextKey, questionId: null, baseId: null });

  const updateRow = (row: CustomBaseRow, patch: Partial<CustomBaseRow>) => {
    Object.assign(row, patch);
    commit();
  };

  const removeRow = (row: CustomBaseRow) => {
    rows.value = rows.value.filter((r) => r.key !== row.key);
    commit();
  };

  const toId = (value: unknown) => (value ? Number(value) : null);

  const questionOptionsFor = (row: CustomBaseRow, options: SelectOption[]) => {
    const taken = new Set(rows.value.filter((r) => r.key !== row.key).map((r) => r.questionId));
    return options.filter((option) => !taken.has(Number(option.value)));
  };
  const baseOptionsFor = (row: CustomBaseRow, options: SelectOption[]) =>
    options.filter((option) => Number(option.value) !== row.questionId);
</script>

<template>
  <div class="space-y-3">
    <div
      v-for="(row, index) in rows"
      :key="row.key"
      class="flex items-end gap-4">
      <div class="min-w-0 flex-1 space-y-2">
        <Label v-if="index === 0">Question</Label>
        <Select
          :model-value="row.questionId"
          :options="questionOptionsFor(row, questionOptions)"
          filterable
          placeholder="Select a question"
          class="w-full"
          @update:model-value="updateRow(row, { questionId: toId($event) })" />
      </div>
      <div class="min-w-0 flex-1 space-y-2">
        <Label v-if="index === 0">Base question</Label>
        <Select
          :model-value="row.baseId"
          :options="baseOptionsFor(row, baseOptions)"
          filterable
          placeholder="Select a base question"
          class="w-full"
          @update:model-value="updateRow(row, { baseId: toId($event) })" />
      </div>
      <Button
        variant="ghost"
        size="icon"
        class="shrink-0"
        @click="removeRow(row)">
        <Trash2 class="size-4" />
      </Button>
    </div>

    <Button
      variant="outline"
      size="sm"
      @click="addRow">
      <Plus class="mr-2 size-4" />
      Add custom base
    </Button>
  </div>
</template>
